import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, X, ShoppingCart, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { cartItems, updateQuantity, removeFromCart, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  if (cartItems.length === 0) {
    return (
      <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center py-20">
          <ShoppingCart className="w-16 h-16 text-gray-700 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Cart is Empty
          </h2>
          <p className="text-gray-500 mb-8">Explore our collection and find something you love.</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
          >
            Continue Shopping <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] min-h-screen pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => (
              <div key={item.id} className="bg-[#111] border border-gray-800/50 rounded-xl p-4 flex gap-4">
                <Link to={`/product/${item.slug}`} className="flex-shrink-0">
                  <img src={item.image} alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.slug}`}
                    className="text-white font-semibold text-sm hover:text-[#2EC866] transition-colors line-clamp-2"
                  >
                    {item.name}
                  </Link>
                  <p className="text-gray-500 text-xs mt-1">{item.categoryName}</p>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center bg-[#0a0a0a] border border-gray-800 rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="px-2.5 py-2 text-gray-400 hover:text-white transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 text-white text-sm font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="px-2.5 py-2 text-gray-400 hover:text-white transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-white font-bold">&pound;{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeFromCart(item.id)}
                  className="text-gray-600 hover:text-red-400 transition-colors self-start p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#111] border border-gray-800/50 rounded-xl p-6 sticky top-28">
              <h3 className="text-white font-bold text-lg mb-6">Order Summary</h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-white">&pound;{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Shipping</span>
                  <span className="text-[#2EC866] font-medium">Free</span>
                </div>
                <div className="border-t border-gray-800 pt-3">
                  <div className="flex justify-between">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-white font-bold text-xl">&pound;{cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold py-3.5 rounded-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(46,200,102,0.2)] flex items-center justify-center gap-2"
              >
                Proceed to Checkout <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/shop" className="block text-center text-gray-500 hover:text-[#2EC866] text-sm mt-4 transition-colors">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
